import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CirclePlus,
  Droplets,
  FilePlus,
  Info,
  Leaf,
  Minus,
  Package,
  Plus,
  RotateCcw,
  Send,
  SprayCan,
  Sprout,
  Tractor,
  Wheat,
} from 'lucide-react';
import { defineMessages } from '../../i18n/define';
import type { Message } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { COMMON } from '../../i18n/common';
import { tr } from '../../i18n/runtime';
import { PageHeader } from '../ui/PageHeader';
import { Card, Stack } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Disclosure } from '../ui/Disclosure';
import { EvidenceMark } from '../ui/EvidenceMark';
import { Icon } from '../ui/Icon';
import { DataTable, type Column } from '../ui/DataTable';
import { DataGate } from './parts';
import { usePageTrail } from '../shell/ShellContext';
import { useTrackData } from '../data/useTrackData';
import { FILES } from '../data/loader';
import { countOf, dateShort, num, windowLabel, WORDS } from '../lib/format';
import type {
  DateWindow,
  Meta,
  TemplateStage,
  TemplateWork,
  TemplatesFile,
  WorkKind,
} from '../data/types';
import './work.css';

const m = defineMessages({
  noteCount: {
    id: 'template.detail.noteCount',
    defaultMessage: '{count, plural, one {# оговорка} few {# оговорки} many {# оговорок} other {# оговорки}}',
    description: 'Число оговорок источника шаблона в сводке раскрывающегося блока; {count} — число',
  },
  stageCount: {
    id: 'template.detail.stageCount',
    defaultMessage: '{count, plural, one {# этап} few {# этапа} many {# этапов} other {# этапа}}',
    description: 'Число этапов шаблона в шапке страницы; {count} — число',
  },
  timesExact: {
    id: 'template.detail.times.exact',
    defaultMessage: '{count, plural, one {# раз} few {# раза} many {# раз} other {# раза}}',
    description: 'Кратность работы в таблице шаблона, одно число: «1 раз», «3 раза»; {count} — число',
  },
  timesRange: {
    id: 'template.detail.times.range',
    defaultMessage: '{min}–{max, plural, one {# раз} few {# раза} many {# раз} other {# раза}}',
    description: 'Кратность работы в таблице шаблона, диапазон: «2–4 раза»; {min} — нижняя граница строкой, {max} — верхняя (число)',
  },
  perHaWater: { id: 'template.detail.perHa.water', defaultMessage: '{value} м³/га', description: 'Норма воды на гектар в таблице шаблона; {value} — число строкой' },
  perHaWaterNote: { id: 'template.detail.perHa.waterNote', defaultMessage: '{water} · {note}', description: 'Норма в таблице шаблона: вода и пояснение через точку; {water} — «1 200 м³/га», {note} — пояснение из данных' },
  kindTillage: { id: 'template.detail.kind.tillage', defaultMessage: 'обработка почвы', description: 'Вид работы: обработка почвы; подсказка у иконки и пункт списка' },
  kindSowing: { id: 'template.detail.kind.sowing', defaultMessage: 'посев', description: 'Вид работы: посев; подсказка у иконки и пункт списка' },
  kindFertilize: { id: 'template.detail.kind.fertilize', defaultMessage: 'питание', description: 'Вид работы: питание растений (удобрение); подсказка у иконки и пункт списка' },
  kindIrrigation: { id: 'template.detail.kind.irrigation', defaultMessage: 'полив', description: 'Вид работы: полив; подсказка у иконки и пункт списка' },
  kindProtection: { id: 'template.detail.kind.protection', defaultMessage: 'защита', description: 'Вид работы: защита растений; подсказка у иконки и пункт списка' },
  kindHarvest: { id: 'template.detail.kind.harvest', defaultMessage: 'уборка', description: 'Вид работы: уборка урожая; подсказка у иконки и пункт списка' },
  kindOther: { id: 'template.detail.kind.other', defaultMessage: 'прочее', description: 'Вид работы: прочее; подсказка у иконки и пункт списка' },
  stepEarlier: { id: 'template.detail.stepper.earlier', defaultMessage: '{label}: на день раньше', description: 'aria-label кнопки «−» у счётчика сдвига даты; {label} — что сдвигается' },
  stepLater: { id: 'template.detail.stepper.later', defaultMessage: '{label}: на день позже', description: 'aria-label кнопки «+» у счётчика сдвига даты; {label} — что сдвигается' },
  errName: { id: 'template.detail.add.errName', defaultMessage: 'Укажите название работы.', description: 'Ошибка формы добавления работы в черновик: название пустое' },
  errWindow: { id: 'template.detail.add.errWindow', defaultMessage: 'Проверьте окно: начало не может быть позже конца.', description: 'Ошибка формы добавления работы в черновик: начало окна позже конца' },
  fieldName: { id: 'template.detail.add.name', defaultMessage: 'Название', description: 'Подпись поля формы добавления работы: название работы' },
  fieldNamePlaceholder: { id: 'template.detail.add.namePlaceholder', defaultMessage: 'например, обработка от блошки', description: 'Подсказка в пустом поле названия новой работы; пример' },
  fieldKind: { id: 'template.detail.add.kind', defaultMessage: 'Вид', description: 'Подпись списка формы добавления работы: вид работы' },
  fieldFrom: { id: 'template.detail.add.from', defaultMessage: 'Начало', description: 'Подпись поля формы добавления работы: начало окна' },
  fieldTo: { id: 'template.detail.add.to', defaultMessage: 'Конец', description: 'Подпись поля формы добавления работы: конец окна' },
  addWork: { id: 'template.detail.add.submit', defaultMessage: 'Добавить работу', description: 'Кнопка формы добавления работы в черновик правок шаблона' },
  draftSource: { id: 'template.detail.add.source', defaultMessage: 'добавлено в черновике (мок)', description: 'Источник новой работы, добавленной в черновик правок; мок — без сохранения' },
  colWork: { id: 'template.detail.col.work', defaultMessage: 'Работа', description: 'Колонка таблицы этапа шаблона: название работы' },
  added: { id: 'template.detail.badge.added', defaultMessage: 'добавлено', description: 'Бейдж у работы, добавленной в черновик правок' },
  colWindow: { id: 'template.detail.col.window', defaultMessage: 'Окно', description: 'Колонка таблицы этапа шаблона: окно выполнения работы' },
  colTimes: { id: 'template.detail.col.times', defaultMessage: 'Кратность', description: 'Колонка таблицы этапа шаблона: сколько раз выполняется работа' },
  colNorm: { id: 'template.detail.col.norm', defaultMessage: 'Норма', description: 'Колонка таблицы этапа шаблона: норма на гектар' },
  colCond: { id: 'template.detail.col.cond', defaultMessage: 'Условность', description: 'Колонка таблицы этапа шаблона: условная ли работа' },
  conditional: { id: 'template.detail.cond.yes', defaultMessage: 'условная', description: 'Бейдж: работа назначается только при условии; коротко' },
  unconditional: { id: 'template.detail.cond.no', defaultMessage: 'безусловная', description: 'Подпись: работа назначается всегда; коротко' },
  colEdit: { id: 'template.detail.col.edit', defaultMessage: 'Правка черновика', description: 'Колонка таблицы в режиме черновика: сдвиг дат и включение работы (менеджер)' },
  editFrom: { id: 'template.detail.edit.from', defaultMessage: 'начало', description: 'Подпись счётчика сдвига начала окна в режиме черновика' },
  editTo: { id: 'template.detail.edit.to', defaultMessage: 'конец', description: 'Подпись счётчика сдвига конца окна в режиме черновика' },
  shiftFrom: { id: 'template.detail.edit.shiftFrom', defaultMessage: '{name}: сдвиг начала', description: 'aria-label счётчика сдвига начала окна; {name} — название работы' },
  shiftTo: { id: 'template.detail.edit.shiftTo', defaultMessage: '{name}: сдвиг конца', description: 'aria-label счётчика сдвига конца окна; {name} — название работы' },
  includeInDraft: { id: 'template.detail.edit.include', defaultMessage: 'включена в черновик', description: 'Подпись флажка условной работы в режиме черновика' },
  summaryDraft: {
    id: 'template.detail.summary.draft',
    defaultMessage: '{active} из {total, plural, one {# работы} other {# работ}} в черновике, {window}',
    description: 'Сводка этапа в режиме черновика правок; {active} — сколько работ включено (строкой), {total} — всего (число), {window} — окно дат',
  },
  summaryPlain: { id: 'template.detail.summary.plain', defaultMessage: '{works}, {window}', description: 'Сводка свёрнутого этапа шаблона; {works} — «5 работ», {window} — окно дат' },
  denomWorks: {
    id: 'template.detail.denom.works',
    defaultMessage: '{count, plural, one {работа} few {работы} many {работ} other {работы}}',
    description: 'Слово-знаменатель под таблицей этапа («показано 5 из 12 работ»); без числа',
  },
  stageEmptyTitle: { id: 'template.detail.stageEmpty.title', defaultMessage: 'Работ в этапе нет', description: 'Заголовок пустой таблицы этапа шаблона' },
  stageEmptyReason: { id: 'template.detail.stageEmpty.reason', defaultMessage: 'шаблон этапа пуст в текущем срезе', description: 'Причина пустой таблицы этапа шаблона' },
  levelDistrict: { id: 'template.detail.trail.district', defaultMessage: 'Район', description: 'Уровень в цепочке навигации над заголовком: район' },
  districtName: { id: 'template.detail.trail.districtName', defaultMessage: 'Бука', description: 'Название района (Бука) в цепочке навигации; географическое имя — транслитерация' },
  levelSection: { id: 'template.detail.trail.section', defaultMessage: 'Раздел', description: 'Уровень в цепочке навигации над заголовком: раздел приложения' },
  levelTemplate: { id: 'template.detail.trail.template', defaultMessage: 'Шаблон', description: 'Уровень в цепочке навигации над заголовком: шаблон агроцикла' },
  trailCycles: { id: 'template.detail.trail.cycles', defaultMessage: 'Агроциклы', description: 'Имя раздела «Агроциклы» в цепочке навигации' },
  titleNamed: { id: 'template.detail.title.named', defaultMessage: 'Шаблон: {crop} {season}', description: 'Заголовок страницы шаблона; {crop} — культура, {season} — подпись сезона из данных' },
  titleFallback: { id: 'template.detail.title.fallback', defaultMessage: 'Шаблон {id}', description: 'Заголовок страницы, пока шаблон не прочитан; {id} — ключ шаблона из адреса' },
  purpose: { id: 'template.detail.purpose', defaultMessage: 'Этапы и работы с окнами. Условные работы помечены отдельно — они не всегда назначаются.', description: 'Подзаголовок страницы шаблона агроцикла' },
  statusActive: { id: 'template.detail.status.active', defaultMessage: 'действует', description: 'Пометка рядом с номером ревизии шаблона: эта ревизия действует' },
  statusAppliedMock: { id: 'template.detail.status.appliedMock', defaultMessage: 'действует (мок)', description: 'Пометка рядом с номером ревизии шаблона после применения правок в моке (без сохранения)' },
  openStageNote: {
    id: 'template.detail.openStageNote',
    defaultMessage: 'Сразу раскрыт этап, в чьё окно попадает дата среза {date}; если срез лежит за краем шаблона — последний этап.',
    description: 'Подпись под шапкой шаблона: какой этап раскрыт сразу; {date} — дата среза',
  },
  notesLabel: { id: 'template.detail.notes.label', defaultMessage: 'Оговорки источников', description: 'Название раскрывающегося блока с оговорками источников шаблона' },
  notesHonesty: {
    id: 'template.detail.notes.honesty',
    defaultMessage: 'Честность прототипа: карта хлопка/пшеницы обобщает исследовательскую выжимку, а не публикует официальный документ. Все оговорки источника — списком, ничего не спрятано.',
    description: 'Пояснение в блоке оговорок шаблона: шаблон — обобщение исследования, а не официальный документ',
  },
  whatTemplate: { id: 'template.detail.what', defaultMessage: 'шаблон ждёт {file}', description: 'Что не удалось показать (подставляется в сообщение о пустых данных): шаблон; {file} — адрес файла данных, не переводить' },
  notFound: { id: 'template.detail.notFound', defaultMessage: 'Шаблона с идентификатором «{id}» в срезе нет. Проверьте адрес или трек данных.', description: 'Сообщение, когда шаблон по адресу не найден; {id} — ключ из адреса' },
  panelTitle: { id: 'template.detail.panel.title', defaultMessage: 'Панель правки', description: 'Заголовок карточки с панелью правки шаблона (менеджер)' },
  panelMock: {
    id: 'template.detail.panel.mock',
    defaultMessage: 'Мок без сохранения: сдвиг окна, включение условных работ и добавление новых работ живут только в состоянии этой вкладки браузера.',
    description: 'Пояснение на панели правки: правки не сохраняются на сервере',
  },
  panelStartDraft: { id: 'template.detail.panel.startDraft', defaultMessage: 'Черновик правок шаблона', description: 'Кнопка на панели правки: начать черновик правок шаблона (менеджер)' },
  draftChip: { id: 'template.detail.panel.draftChip', defaultMessage: 'черновик правок — не применён', description: 'Чип на панели правки: черновик ещё не применён' },
  appliedChip: { id: 'template.detail.panel.appliedChip', defaultMessage: 'правки применены (мок)', description: 'Метка на панели правки: правки применены в моке, без сохранения' },
  draftNote: {
    id: 'template.detail.panel.draftNote',
    defaultMessage: 'Правки видны прямо в таблицах этапов выше: сдвиг окна, выключенные условные работы и добавленные строки помечены отдельно. {tail}',
    description: 'Пояснение на панели правки в режиме черновика; {tail} — одна из двух фраз ниже',
  },
  draftTailApplied: { id: 'template.detail.panel.tailApplied', defaultMessage: 'Применение правок — мок: обновление страницы вернёт шаблон к исходной ревизии.', description: 'Продолжение пояснения на панели правки после применения (мок)' },
  draftTailDraft: { id: 'template.detail.panel.tailDraft', defaultMessage: 'Ничего не отправляется на сервер: обновление страницы отменит черновик.', description: 'Продолжение пояснения на панели правки в режиме черновика' },
  apply: { id: 'template.detail.panel.apply', defaultMessage: 'Применить правки', description: 'Кнопка на панели правки: открыть подтверждение применения правок шаблона (менеджер)' },
  cancelDraft: { id: 'template.detail.panel.cancelDraft', defaultMessage: 'Отменить черновик', description: 'Кнопка на панели правки: отбросить черновик правок' },
  dialogTitle: { id: 'template.detail.dialog.title', defaultMessage: 'Применение правок шаблона', description: 'Заголовок окна подтверждения применения правок шаблона' },
  dialogBody: {
    id: 'template.detail.dialog.body',
    defaultMessage: 'Правки создадут новую ревизию шаблона. Идущие кампании остаются на той ревизии, по которой были запущены: их окна и работы на полях новая ревизия не переписывает.',
    description: 'Текст окна подтверждения применения правок шаблона (менеджер)',
  },
});

const KIND_MESSAGE: Record<WorkKind, Message> = {
  tillage: m.kindTillage,
  sowing: m.kindSowing,
  fertilize: m.kindFertilize,
  irrigation: m.kindIrrigation,
  protection: m.kindProtection,
  harvest: m.kindHarvest,
  other: m.kindOther,
};

/** Подпись вида работы: «обработка почвы», «посев»… */
function kindLabel(kind: WorkKind): string {
  return tr(KIND_MESSAGE[kind]);
}

/** Дата среза мира прототипа, пока meta.json не прочитан. */
const AS_OF_FALLBACK = '2026-08-26';

const KIND_ICON: Record<WorkKind, LucideIcon> = {
  tillage: Tractor,
  sowing: Sprout,
  fertilize: Leaf,
  irrigation: Droplets,
  protection: SprayCan,
  harvest: Wheat,
  other: Package,
};

const KIND_ORDER: WorkKind[] = ['tillage', 'sowing', 'fertilize', 'irrigation', 'protection', 'harvest', 'other'];

type PanelMode = 'idle' | 'draft' | 'published';

interface OffsetEdit {
  fromOffset: number;
  toOffset: number;
}

type StageWorkRow = TemplateWork & { draftAdded?: boolean };

interface DraftWork {
  id: string;
  stageId: string;
  name: string;
  kind: WorkKind;
  window: DateWindow;
}

/** Сдвиг ISO-даты на N дней (может быть отрицательным). */
function shiftIso(iso: string, days: number): string {
  if (!days) return iso;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function shiftWindow(window: DateWindow, offset: OffsetEdit | undefined): DateWindow {
  if (!offset) return window;
  return { from: shiftIso(window.from, offset.fromOffset), to: shiftIso(window.to, offset.toOffset) };
}

/** «2–4 раза» / «1 раз» — согласованная кратность, а не голый диапазон. */
function countRangeLabel([min, max]: [number, number]): string {
  if (min === max) return tr(m.timesExact, { count: min });
  return tr(m.timesRange, { min: String(min), max });
}

function perHaLabel(perHa: Record<string, string | number | null>): string {
  const water = perHa.water_m3;
  const note = perHa.note;
  const waterText = typeof water === 'number' ? tr(m.perHaWater, { value: num(water) }) : null;
  if (waterText && note) return tr(m.perHaWaterNote, { water: waterText, note: String(note) });
  if (waterText) return waterText;
  if (typeof note === 'string' && note) return note;
  return '—';
}

function Stepper({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (delta: number) => void;
}) {
  const t = useT();
  return (
    <span className="stepper" role="group" aria-label={label}>
      <button
        type="button"
        className="stepper__btn focus-ring"
        onClick={() => onChange(-1)}
        aria-label={t(m.stepEarlier, { label })}
      >
        <Icon icon={Minus} size={16} />
      </button>
      <span className="stepper__value num" aria-live="polite">
        {value > 0 ? `+${value}` : value}
      </span>
      <button
        type="button"
        className="stepper__btn focus-ring"
        onClick={() => onChange(1)}
        aria-label={t(m.stepLater, { label })}
      >
        <Icon icon={Plus} size={16} />
      </button>
    </span>
  );
}

function AddWorkForm({ stage, onAdd }: { stage: TemplateStage; onAdd: (work: DraftWork) => void }) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="addwork"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const name = String(data.get('name') ?? '').trim();
        const kind = String(data.get('kind') ?? 'other') as WorkKind;
        const from = String(data.get('from') ?? '');
        const to = String(data.get('to') ?? '');

        if (!name) {
          setError(t(m.errName));
          return;
        }
        if (!from || !to || from > to) {
          setError(t(m.errWindow));
          return;
        }

        setError(null);
        onAdd({
          id: `draft-${stage.id}-${Date.now().toString(36)}`,
          stageId: stage.id,
          name,
          kind,
          window: { from, to },
        });
        event.currentTarget.reset();
      }}
    >
      <label className="addwork__field">
        {t(m.fieldName)}
        <input type="text" name="name" placeholder={t(m.fieldNamePlaceholder)} required />
      </label>
      <label className="addwork__field">
        {t(m.fieldKind)}
        <select name="kind" defaultValue="other">
          {KIND_ORDER.map((kind) => (
            <option key={kind} value={kind}>
              {kindLabel(kind)}
            </option>
          ))}
        </select>
      </label>
      <label className="addwork__field">
        {t(m.fieldFrom)}
        <input type="date" name="from" defaultValue={stage.window.from} required />
      </label>
      <label className="addwork__field">
        {t(m.fieldTo)}
        <input type="date" name="to" defaultValue={stage.window.to} required />
      </label>
      <button type="submit" className="btn btn--secondary btn--sm focus-ring">
        <Icon icon={CirclePlus} size={16} /> {t(m.addWork)}
      </button>
      {error ? (
        <span className="addwork__error t-caption" role="alert">
          {error}
        </span>
      ) : null}
    </form>
  );
}

function StageBlock({
  stage,
  mode,
  offsets,
  disabledWorks,
  addedWorks,
  defaultOpen,
  onAdjust,
  onToggle,
  onAddWork,
}: {
  stage: TemplateStage;
  mode: PanelMode;
  offsets: Record<string, OffsetEdit>;
  disabledWorks: Record<string, boolean>;
  addedWorks: DraftWork[];
  /** Стадия на дату среза открыта сразу: экран без единого ответа — не экран. */
  defaultOpen?: boolean;
  onAdjust: (workId: string, key: 'fromOffset' | 'toOffset', delta: number) => void;
  onToggle: (workId: string) => void;
  onAddWork: (work: DraftWork) => void;
}) {
  const t = useT();
  const editing = mode === 'draft';
  const totalCount = stage.works.length + addedWorks.length;
  const activeCount = stage.works.filter((w) => !disabledWorks[w.id]).length + addedWorks.length;
  const changedInStage = stage.works.some((w) => disabledWorks[w.id]) || addedWorks.length > 0;

  const rows: StageWorkRow[] = [
    ...stage.works,
    ...addedWorks.map<StageWorkRow>((w) => ({
      id: w.id,
      name: w.name,
      kind: w.kind,
      window: w.window,
      per_ha: {},
      count_range: [1, 1],
      conditional: null,
      source: t(m.draftSource),
      draftAdded: true,
    })),
  ];

  const columns: Column<StageWorkRow>[] = [
    {
      key: 'name',
      header: t(m.colWork),
      render: (row) => {
        const off = disabledWorks[row.id];
        return (
          <span className={`workrow ${off ? 'workrow--off' : ''}`}>
            <span className="workrow__name">
              <Icon icon={KIND_ICON[row.kind]} size={16} title={kindLabel(row.kind)} />
              {row.name}
              {row.draftAdded ? <Badge tone="green">{t(m.added)}</Badge> : null}
            </span>
            <span className="workrow__source t-caption">{row.source}</span>
          </span>
        );
      },
    },
    {
      key: 'window',
      header: t(m.colWindow),
      render: (row) => {
        const w = shiftWindow(row.window, offsets[row.id]);
        return windowLabel(w.from, w.to);
      },
    },
    {
      key: 'count',
      header: t(m.colTimes),
      render: (row) => countRangeLabel(row.count_range),
    },
    {
      key: 'norm',
      header: t(m.colNorm),
      render: (row) => perHaLabel(row.per_ha),
    },
    {
      key: 'cond',
      header: t(m.colCond),
      render: (row) =>
        row.conditional ? (
          <span className="workrow__cond">
            {/* Условность — свойство работы, а не тревога: нейтральный значок
                с иконкой, медь тут была статусом и уходит. */}
            <Badge tone="neutral" icon={Info}>
              {t(m.conditional)}
            </Badge>
            <span className="t-caption">{row.conditional}</span>
          </span>
        ) : (
          <span className="t-caption">{t(m.unconditional)}</span>
        ),
    },
  ];

  if (editing) {
    columns.push({
      key: 'edit',
      header: t(m.colEdit),
      width: '210px',
      render: (row) => (
        <div className="workedit">
          <div className="workedit__row">
            <span className="workedit__label">{t(m.editFrom)}</span>
            <Stepper
              label={t(m.shiftFrom, { name: row.name })}
              value={offsets[row.id]?.fromOffset ?? 0}
              onChange={(delta) => onAdjust(row.id, 'fromOffset', delta)}
            />
          </div>
          <div className="workedit__row">
            <span className="workedit__label">{t(m.editTo)}</span>
            <Stepper
              label={t(m.shiftTo, { name: row.name })}
              value={offsets[row.id]?.toOffset ?? 0}
              onChange={(delta) => onAdjust(row.id, 'toOffset', delta)}
            />
          </div>
          {row.conditional ? (
            <label className="workedit__toggle">
              <input type="checkbox" checked={!disabledWorks[row.id]} onChange={() => onToggle(row.id)} />
              {t(m.includeInDraft)}
            </label>
          ) : null}
        </div>
      ),
    });
  }

  const summary =
    mode !== 'idle' && changedInStage
      ? t(m.summaryDraft, {
          active: String(activeCount),
          total: totalCount,
          window: windowLabel(stage.window.from, stage.window.to),
        })
      : t(m.summaryPlain, {
          works: countOf(stage.works.length, WORDS.work),
          window: windowLabel(stage.window.from, stage.window.to),
        });

  return (
    <Disclosure label={stage.name} summary={summary} defaultOpen={defaultOpen}>
      <DataTable
        columns={columns}
        rows={rows}
        getKey={(row) => row.id}
        denominator={m.denomWorks}
        empty={{ title: t(m.stageEmptyTitle), reason: t(m.stageEmptyReason) }}
      />
      {editing ? <AddWorkForm stage={stage} onAdd={onAddWork} /> : null}
    </Disclosure>
  );
}

export function TemplateDetail() {
  const t = useT();
  const { id = '' } = useParams();
  // Набор, а не адрес: на канонических маршрутах тот же `templates.json`
  // собирает адаптер из `/agrocycle/templates`, и экран об этом не знает.
  const templates = useTrackData<TemplatesFile>(FILES.templates);
  const meta = useTrackData<Meta>(FILES.meta);
  const template = (templates.data?.templates ?? []).find((tpl) => tpl.id === id) ?? null;
  const asOf = meta.data?.as_of ?? AS_OF_FALLBACK;

  const stageCount = template?.stages.length ?? 0;
  const workCount = template
    ? template.stages.reduce((sum, stage) => sum + stage.works.length, 0)
    : 0;

  /**
   * Открытой встречает стадия, в чьё окно попадает дата среза; окна стадий
   * перекрываются, поэтому берётся последняя подходящая. У закрытого шаблона
   * (пшеница закончилась в июле) срез лежит за краем — открывается последняя.
   */
  const openStageId = useMemo(() => {
    const stages = template?.stages ?? [];
    if (stages.length === 0) return null;
    const covering = [...stages].reverse().find((s) => s.window.from <= asOf && asOf <= s.window.to);
    return (covering ?? stages[stages.length - 1]!).id;
  }, [template, asOf]);

  const [mode, setMode] = useState<PanelMode>('idle');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [offsets, setOffsets] = useState<Record<string, OffsetEdit>>({});
  const [disabledWorks, setDisabledWorks] = useState<Record<string, boolean>>({});
  const [draftWorks, setDraftWorks] = useState<DraftWork[]>([]);

  const publishTriggerRef = useRef<HTMLButtonElement | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);
  /** Запасной приёмник фокуса: после «Опубликовать» кнопка-триггер сама
   * исчезает вместе с драфт-режимом в том же обновлении, так что фокус
   * возвращается на панель, а не теряется. */
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Черновик привязан к конкретному шаблону: смена id (другой /templates/:id)
  // не должна тащить за собой чужие правки — это тот же принцип, что и
  // локальный фильтр экрана, который не едет с переходом на другую единицу.
  useEffect(() => {
    setMode('idle');
    setConfirmOpen(false);
    setOffsets({});
    setDisabledWorks({});
    setDraftWorks([]);
  }, [id]);

  useEffect(() => {
    if (!confirmOpen) return undefined;
    confirmBtnRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setConfirmOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      (publishTriggerRef.current ?? panelRef.current)?.focus();
    };
  }, [confirmOpen]);

  usePageTrail([
    // i18n-ignore: запасное имя района — собственное название, а не текст интерфейса
    { level: t(m.levelDistrict), name: t(m.districtName), to: '/' },
    { level: t(m.levelSection), name: t(m.trailCycles), to: '/cycles' },
    { level: t(m.levelTemplate), name: template ? `${template.crop} ${template.season_label}` : id },
  ]);

  function adjustOffset(workId: string, key: 'fromOffset' | 'toOffset', delta: number) {
    setOffsets((prev) => {
      const cur = prev[workId] ?? { fromOffset: 0, toOffset: 0 };
      return { ...prev, [workId]: { ...cur, [key]: cur[key] + delta } };
    });
  }

  function toggleWork(workId: string) {
    setDisabledWorks((prev) => ({ ...prev, [workId]: !prev[workId] }));
  }

  function addWork(work: DraftWork) {
    setDraftWorks((prev) => [...prev, work]);
  }

  function cancelDraft() {
    setMode('idle');
    setConfirmOpen(false);
    setOffsets({});
    setDisabledWorks({});
    setDraftWorks([]);
  }

  const version = mode === 'published' ? 'v2' : template?.version ?? 'v1';

  return (
    <Stack>
      <PageHeader
        level="district"
        title={template ? t(m.titleNamed, { crop: template.crop, season: template.season_label }) : t(m.titleFallback, { id })}
        purpose={t(m.purpose)}
      >
        {/* Шапка отвечает числом, а не одним заголовком: сколько этапов,
            сколько работ и какая ревизия действует. */}
        {template ? (
          <>
            <div className="tplfacts" data-guide="facts">
              <strong>{t(m.stageCount, { count: stageCount })}</strong>
              <span className="tplfacts__sep" aria-hidden="true">
                ·
              </span>
              <strong>{countOf(workCount, WORDS.work)}</strong>
              <span className="tplfacts__sep" aria-hidden="true">
                ·
              </span>
              <span>
                <strong>{version}</strong>{' '}
                {mode === 'published' ? t(m.statusAppliedMock) : t(m.statusActive)}
              </span>
            </div>
            <p className="t-caption">
              {t(m.openStageNote, { date: dateShort(asOf) })}
            </p>
          </>
        ) : null}
      </PageHeader>

      {template && template.notes.length > 0 ? (
        <div data-guide="notes"><Disclosure label={t(m.notesLabel)} summary={t(m.noteCount, { count: template.notes.length })}>
          <p className="t-caption" style={{ marginBottom: 'var(--sp-3)' }}>
            {t(m.notesHonesty)}
          </p>
          <ul className="facts">
            {template.notes.map((note, i) => (
              <li className="facts__row" key={i}>
                <span className="facts__key">{i + 1}</span>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </Disclosure></div>
      ) : null}

      <DataGate res={templates} what={t(m.whatTemplate, { file: '/data/templates.json' })}>
        {() =>
          template ? (
            <div data-guide="stages"><Stack tight>
              {template.stages.map((stage) => (
                <StageBlock
                  key={stage.id}
                  stage={stage}
                  mode={mode}
                  offsets={offsets}
                  disabledWorks={disabledWorks}
                  defaultOpen={stage.id === openStageId}
                  addedWorks={draftWorks.filter((w) => w.stageId === stage.id)}
                  onAdjust={adjustOffset}
                  onToggle={toggleWork}
                  onAddWork={addWork}
                />
              ))}
            </Stack></div>
          ) : (
            <Card>
              <p className="t-caption">
                {t(m.notFound, { id })}
              </p>
            </Card>
          )
        }
      </DataGate>

      {template ? (
        <div data-guide="editor"><Card size="lg" title={t(m.panelTitle)}>
          {mode === 'idle' ? (
            <div className="editpanel" ref={panelRef} tabIndex={-1}>
              <p className="t-caption">
                {t(m.panelMock)}
              </p>
              <div className="editpanel__actions">
                <button type="button" className="btn btn--secondary focus-ring" onClick={() => setMode('draft')}>
                  <Icon icon={FilePlus} size={18} /> {t(m.panelStartDraft)}
                </button>
              </div>
            </div>
          ) : (
            <div className="editpanel" ref={panelRef} tabIndex={-1}>
              <div className="editpanel__banner">
                {mode === 'draft' ? (
                  // Черновик — не «внимание», а незавершённость: нейтральный чип
                  // с пунктирной рамкой. Медь оставлена GIS-кандидату.
                  <span className="draftchip">
                    <Icon icon={FilePlus} size={16} />
                    {t(m.draftChip)}
                  </span>
                ) : (
                  <EvidenceMark kind="demo" label={t(m.appliedChip)} />
                )}
              </div>
              <p className="t-caption">
                {t(m.draftNote, { tail: mode === 'published' ? t(m.draftTailApplied) : t(m.draftTailDraft) })}
              </p>
              {mode === 'draft' ? (
                <div className="editpanel__actions">
                  <button
                    type="button"
                    className="btn btn--secondary focus-ring"
                    ref={publishTriggerRef}
                    onClick={() => setConfirmOpen(true)}
                  >
                    <Icon icon={Send} size={18} /> {t(m.apply)}
                  </button>
                  <button type="button" className="btn btn--ghost focus-ring" onClick={cancelDraft}>
                    <Icon icon={RotateCcw} size={18} /> {t(m.cancelDraft)}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </Card></div>
      ) : null}

      {confirmOpen ? (
        <div
          className="dialog-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setConfirmOpen(false);
          }}
        >
          <div className="dialog-card" role="dialog" aria-modal="true" aria-labelledby="publish-dialog-title">
            <h2 id="publish-dialog-title" className="t-title">
              {t(m.dialogTitle)}
            </h2>
            <p className="t-body">{t(m.dialogBody)}</p>
            <div className="dialog-card__actions">
              <button type="button" className="btn btn--ghost focus-ring" onClick={() => setConfirmOpen(false)}>
                {t(COMMON.cancel)}
              </button>
              <button
                type="button"
                className="btn btn--secondary focus-ring"
                ref={confirmBtnRef}
                onClick={() => {
                  setMode('published');
                  setConfirmOpen(false);
                }}
              >
                <Icon icon={Send} size={18} /> {t(m.apply)}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Stack>
  );
}
